import { QBS_DETAILS_DATA_TEST_ID } from '@e2e/constants/exam-management';
import {
  GetQBankStructureLevelItemsAbilityByQBankStructureIdDocument,
  GetQBankStructureLevelItemsByQBankStructureIdDocument,
  QBankStructureStatus,
  QBankStructureValidationCase,
  type SaveAsLevelItemInput,
  useSaveAsUnpublishedMutation,
  useSaveQBankStructureAsDraftMutation,
  useValidateQBankStructureBeforePublishLazyQuery,
} from '@exam-management/graphql';
import { APP_PERMISSIONS } from '@exam-management/permissions';
import { useQBankStructure } from '@exam-management/qbs-provider';
import { Button, ConfirmationModal, Divider, Flex, Form, Paper } from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useHasPermission } from '@shared/permissions';
import { useSnackbar } from '@shared/snackbar';
import { useCallback, useImperativeHandle, useMemo } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';

import {
  QBANK_STRUCTURE_LEVEL_ITEM_FORM_ACTION_QUERY_KEY,
  QBankStructureLevelItemFormAction,
} from '../../constants';
import { useEditingField } from '../../hooks';
import {
  EditQBankStructureLevelItemProvider,
  type QBankStructureLevelFormInput,
  QBankStructureLevelFormProvider,
  type QBSLevelItemExcludingTag,
  UNSAVED_CHANGES_ERROR_MESSAGE,
  useEditQBSLevelItem,
  useQBankStructureLevelFormContext,
} from '../../providers';
import type { ActionOutletContextType } from '../../types';
import { QBankStructureLevelItemList } from '../qbank-structure-level-item-list';
import { QBankStructureLevelItemListHeader } from '../qbank-structure-level-item-list-header';

function formatLevelItems(
  levelItems: QBSLevelItemExcludingTag[],
  names: Record<string, string | null | undefined>,
): SaveAsLevelItemInput[] {
  return levelItems.map((levelItem) => {
    const name = names[levelItem.id];
    const id = levelItem.id;
    const tag = name ? { name } : undefined;
    const order = levelItem.order ?? 0;
    const parentLevelItemId = levelItem.parentLevelItemId ?? null;
    const qBankStructureLevelId = levelItem.qBankStructureLevelId ?? '';

    return {
      id,
      tag,
      order,
      parentLevelItemId,
      qBankStructureLevelId,
    };
  });
}

function QBankStructureDetail() {
  const { qBankStructureInformation, formMethodsRef, validatePublishQBankStructure } =
    useQBankStructure();
  const { levelItems: qBankStructureLevelItems, resetQBStructureLevelItems } =
    useEditQBSLevelItem();
  const [, setSearchParams] = useSearchParams();
  const { action } = useOutletContext<ActionOutletContextType>();
  const [isCancelModalOpen, cancelModalHandlers] = useDisclosure();
  const { open: openCancelModal, close: closeCancelModal } = cancelModalHandlers;
  const formContext = useQBankStructureLevelFormContext();

  const handleSetToView = () => {
    setSearchParams({
      [QBANK_STRUCTURE_LEVEL_ITEM_FORM_ACTION_QUERY_KEY]: QBankStructureLevelItemFormAction.VIEW,
    });
  };

  const handleCancelEdit = useCallback(() => {
    const values = Object.entries(formContext.getValues());
    const defaultValues = Object.entries({ ...formContext.formState.defaultValues });

    const isDirty = !values.every(([key, value]) => {
      const defaultValue = defaultValues.find(([k]) => k === key);
      return defaultValue && defaultValue[1] === value;
    });

    if (isDirty) {
      openCancelModal();
    } else {
      handleSetToView();
    }
  }, [formContext]);

  const handleDiscardCancelEdit = () => {
    closeCancelModal();
  };

  const handleAcceptCancelEdit = () => {
    handleSetToView();
    formContext.reset();
    resetQBStructureLevelItems();
    closeCancelModal();
  };

  const hasUpdatePermission = useHasPermission(APP_PERMISSIONS.QBANK.STRUCTURE.UPDATE);
  const isEditingMode = action === QBankStructureLevelItemFormAction.EDIT;

  const shouldShowCancelButton = isEditingMode;
  const shouldShowSaveQBankStructureDetailButton =
    (qBankStructureInformation.status === QBankStructureStatus.Draft || isEditingMode) &&
    hasUpdatePermission;

  const { showSnackbar } = useSnackbar();

  const editingFields = useEditingField();

  const saveRefetchQueries = [
    {
      query: GetQBankStructureLevelItemsByQBankStructureIdDocument,
      variables: { qBankStructureId: qBankStructureInformation.id, options: null },
    },
    {
      query: GetQBankStructureLevelItemsAbilityByQBankStructureIdDocument,
      variables: {
        qBankStructureId: qBankStructureInformation.id,
      },
    },
  ];

  const [saveQBankStructureAsDraft, { loading: isSaveQBankStructureAsDraftLoading }] =
    useSaveQBankStructureAsDraftMutation({
      onCompleted: () => {
        showSnackbar({ message: 'QBank Structure has been saved', variant: 'success' });
      },
      onError: () => {
        showSnackbar({ message: 'Unable to Save QBank Structure', variant: 'error' });
      },
      refetchQueries: saveRefetchQueries,
      awaitRefetchQueries: true,
    });

  const [saveQBankStructureAsUnpublish, { loading: isSaveQBankStructureAsUnpublishLoading }] =
    useSaveAsUnpublishedMutation({
      onCompleted: () => {
        showSnackbar({ message: 'QBank Structure has been saved', variant: 'success' });
        handleSetToView();
      },
      onError: () => {
        showSnackbar({ message: 'Unable to Save QBank Structure', variant: 'error' });
      },
      refetchQueries: saveRefetchQueries,
      awaitRefetchQueries: true,
    });

  const [validateBeforePublish, { loading: isValidateBeforePublishLoading }] =
    useValidateQBankStructureBeforePublishLazyQuery();

  const handleSave = useCallback(
    async (data: QBankStructureLevelFormInput) => {
      validatePublishQBankStructure.clearErrors();
      if (editingFields.data.size > 0) {
        for (const key of editingFields.data.keys()) {
          formContext.setError(key, { type: 'onBlur', message: UNSAVED_CHANGES_ERROR_MESSAGE });
        }
        return Promise.reject(new Error(UNSAVED_CHANGES_ERROR_MESSAGE));
      }

      if (qBankStructureInformation.status === QBankStructureStatus.Draft) {
        const levelItems = formatLevelItems(qBankStructureLevelItems, data);

        await saveQBankStructureAsDraft({
          variables: {
            input: {
              levelItems,
              qBankStructureId: qBankStructureInformation.id,
            },
          },
        });
        return Promise.resolve(levelItems);
      }

      if (qBankStructureInformation.status === QBankStructureStatus.Unpublished) {
        const values = formContext.getValues();
        const levelItems = formatLevelItems(qBankStructureLevelItems, values);
        const newLevelItems = formatLevelItems(
          qBankStructureLevelItems.filter((item) => item.isNew),
          values,
        );
        //TODO: validate function may change CC. P'TEE
        //REMARK: Unpublished QBS need to validate all level items as same as publish condition before save
        const { data: validateResultData } = await validateBeforePublish({
          variables: {
            input: {
              id: qBankStructureInformation.id,
              levelItems,
            },
          },
        });
        if (
          validateResultData?.qbsValidateQBankStructureBeforePublish.every(
            (result) => result.passed,
          )
        ) {
          await saveQBankStructureAsUnpublish({
            variables: {
              input: {
                levelItems: newLevelItems,
                qBankStructureId: qBankStructureInformation.id,
              },
            },
          });
          return Promise.resolve(levelItems);
        } else {
          showSnackbar({ message: 'Unable to Save QBank Structure', variant: 'error' });
          const errors =
            validateResultData?.qbsValidateQBankStructureBeforePublish.flatMap((result) =>
              result.issues.map((issue) => ({
                name: issue.levelItemId ?? '',
                type: result.case,
              })),
            ) ?? [];

          const nameErrors = errors.filter(
            (error) => error.type === QBankStructureValidationCase.MissingNameInInformationItem,
          );
          const missingLastItemErrors = errors.filter(
            (error) => error.type === QBankStructureValidationCase.MissingLastLevelItem,
          );

          validatePublishQBankStructure.setMissingLastItemErrors(missingLastItemErrors);
          if (nameErrors.length > 0) {
            for (const error of nameErrors) {
              formContext.setError(error.name, { type: 'required', message: 'กรุณาระบุชื่อ' });
            }
          }
          return Promise.reject(new Error('Unable to Save QBank Structure'));
        }
      }
    },
    [
      editingFields.data,
      formContext,
      qBankStructureInformation,
      qBankStructureLevelItems,
      validatePublishQBankStructure,
    ],
  );

  const isLoading =
    isSaveQBankStructureAsDraftLoading ||
    isSaveQBankStructureAsUnpublishLoading ||
    isValidateBeforePublishLoading;

  useImperativeHandle(
    formMethodsRef,
    () => ({
      formContext,
      onSubmit: handleSave,
    }),
    [formContext, handleSave],
  );

  return (
    <Form onSubmit={formContext.handleSubmit(handleSave)}>
      <Flex
        sx={(theme) => ({
          flexDirection: 'column',
          p: theme.spacing(6),
          gap: theme.spacing(6),
          flex: 1,
        })}
      >
        <Paper
          sx={(theme) => ({
            flexGrow: 1,
            borderRadius: theme.shape.rounded(2),
          })}
        >
          <QBankStructureLevelItemListHeader />
          <Divider />
          <QBankStructureLevelItemList />
        </Paper>
        <Flex sx={(theme) => ({ justifyContent: 'center', gap: theme.spacing(3) })}>
          {shouldShowCancelButton ? (
            <>
              <Button
                color='secondary'
                variant='outlined'
                onClick={handleCancelEdit}
                sx={{
                  minWidth: '140px',
                  minHeight: '44px',
                }}
              >
                Cancel
              </Button>
              <ConfirmationModal
                content='คุณต้องการยกเลิกการเปลี่ยนแปลงโครงสร้างโดยไม่บันทึกใช่หรือไม่'
                title='Cancel Structure Update?'
                onAccept={handleAcceptCancelEdit}
                onDiscard={handleDiscardCancelEdit}
                open={isCancelModalOpen}
              />
            </>
          ) : null}
          {shouldShowSaveQBankStructureDetailButton ? (
            <Button
              type='submit'
              sx={{
                minWidth: '140px',
                minHeight: '44px',
              }}
              loading={isLoading}
              data-testid={QBS_DETAILS_DATA_TEST_ID.SAVE_BUTTON}
            >
              Save
            </Button>
          ) : null}
        </Flex>
      </Flex>
    </Form>
  );
}

export default function QBankStructureDetailLayout() {
  const { qBankStructureLevelItems } = useQBankStructure();

  const initialFormValues = useMemo(() => {
    return qBankStructureLevelItems.reduce((acc: { [key: string]: string }, item) => {
      acc[item.id] = item.tag?.name ?? '';
      return acc;
    }, {});
  }, [qBankStructureLevelItems]);

  return (
    <QBankStructureLevelFormProvider initialValues={initialFormValues}>
      <EditQBankStructureLevelItemProvider initialQBSLevelItems={qBankStructureLevelItems}>
        <QBankStructureDetail />
      </EditQBankStructureLevelItemProvider>
    </QBankStructureLevelFormProvider>
  );
}
